import { Column, Flex, Row } from '@components/layout/grid'
import type { NavigationLinks } from '@components/layout/navigation-links'
import { Socials } from '@components/socials'
import { Text } from '@components/text'
import { ArticleApps } from '@contentful-components/article-apps'
import groupBy from 'lodash/fp/groupBy'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import type { FunctionComponent } from 'react'

import styles from './footer-links.module.scss'

const ConsentButton = dynamic(() => import('../../consent-button'), {
    ssr: false,
})

/**
 * Footer links are dynamic. The links are split up by category and the grid
 * column width for the social links resizes accordingly. It can comfortably
 * fit 3 categories on desktop and mobile, but any more gets a bit squished.
 */
export const FooterLinks: FunctionComponent<FooterLinksProps> = ({ links }) => {
    // Map the navigation links by category to [[ category, items[] ]]
    const linkCategories = Object.entries(groupBy('category', links))
    const totalCategories = linkCategories.length
    const linksColumnSpan = 2
    const socialsColumnSpan = 12 - linksColumnSpan * totalCategories

    return (
        <Row className={styles['links-row']}>
            {linkCategories.map(([category, items], categoryIndex) => (
                <Column
                    span={{
                        _: 12 / totalCategories,
                        md: linksColumnSpan,
                    }}
                    key={`${category}--${categoryIndex}`}
                >
                    <Text
                        typeStyle='body-sm'
                        tag='div'
                        className={styles.category}
                        bold
                    >
                        {category}
                    </Text>
                    <ul className={styles.links}>
                        {items.map(({ label, href }) => (
                            <li className={styles['link-item']} key={href}>
                                <Link href={href}>
                                    <Text
                                        tag='span'
                                        typeStyle='body-xs'
                                        color='dim'
                                    >
                                        {label}
                                    </Text>
                                </Link>
                            </li>
                        ))}
                        {category === 'Legal' && (
                            <li className={styles['link-item']}>
                                <ConsentButton />
                            </li>
                        )}
                    </ul>
                </Column>
            ))}

            {/* Social media icons */}
            <Column span={{ _: 12, md: socialsColumnSpan }}>
                <Flex justify={{ _: 'center' }}>
                    <Socials />
                </Flex>
                <Flex justify={{ _: 'center' }}>
                    <ArticleApps background='dark' />
                </Flex>
            </Column>
        </Row>
    )
}

export interface FooterLinksProps {
    links?: NavigationLinks
}
